import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { voice } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, SimpleGrid } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Modal } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Textarea, Select, NumberInput, Switch, SegmentedControl, Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { StatusPill } from '../../../components/panel';

const MUTED = 'var(--rr-color-text-muted)';
const message = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/**
 * The people who take calls: lines and departments.
 *
 * A line is a phone or app that signs in, or a forward to a mobile or
 * landline. A department is several lines rung together or in order. The
 * assistant can pass a caller to either by name, and to nothing else: it
 * cannot dial a number a caller gives it. A line may call outside numbers
 * only when that is switched on for it, which is off to begin with.
 */
export function Team({ status }: { status: voice.VoiceStatus }) {
  const t = useT();
  const qc = useQueryClient();
  const lines = useQuery({ queryKey: ['voice-extensions'], queryFn: () => voice.listExtensions(), refetchInterval: 10_000 });
  const groups = useQuery({ queryKey: ['voice-groups'], queryFn: () => voice.listGroups() });
  const [line, setLine] = useState<voice.VoiceExtension | 'new' | null>(null);
  const [group, setGroup] = useState<voice.VoiceGroup | 'new' | null>(null);
  const [shown, setShown] = useState<voice.VoiceExtension | null>(null);
  const lineRows = lines.data?.data ?? [];
  const groupRows = groups.data?.data ?? [];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['voice-extensions'] });
    void qc.invalidateQueries({ queryKey: ['voice-groups'] });
  };

  const removeLine = useMutation({
    mutationFn: (x: voice.VoiceExtension) => voice.deleteExtension(x.id),
    onSuccess: () => { refresh(); notifySuccess(t('voice.lines.removed')); },
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });
  const replace = useMutation({
    mutationFn: (x: voice.VoiceExtension) => voice.replaceExtensionSecret(x.id),
    onSuccess: (res) => { refresh(); setShown(res.data); },
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });
  const removeGroup = useMutation({
    mutationFn: (g: voice.VoiceGroup) => voice.deleteGroup(g.id),
    onSuccess: () => { refresh(); notifySuccess(t('voice.groups.removed')); },
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });

  const lineName = (id: string) => { const x = lineRows.find((l) => l.id === id); return x ? `${x.name} (${x.number})` : ''; };

  return (
    <Stack gap="lg">
      {status.outbound_blocked && (
        <Alert color="error" role="alert" data-testid="voice-outbound-blocked">
          <Stack gap={4}>
            <Text role="bodySmall" fw={650}>{t('voice.lines.blocked.title')}</Text>
            <Text role="bodySmall">{status.outbound_blocked_reason ?? ''}</Text>
            <Text role="caption">{t('voice.lines.blocked.help')}</Text>
          </Stack>
        </Alert>
      )}

      <Card withBorder data-testid="voice-lines">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
              <Title order={4}>{t('voice.lines.title')}</Title>
              <Text role="bodySmall" c={MUTED}>{t('voice.lines.intro')}</Text>
            </Stack>
            <Button size="xs" onClick={() => setLine('new')}>{t('voice.lines.add')}</Button>
          </Group>
          {lines.error && <Alert color="error" role="alert">{message(lines.error, t('common.unknownError'))}</Alert>}
          {!lines.isPending && lineRows.length === 0 && <EmptyState title={t('voice.lines.empty')} description={t('voice.lines.emptyHint')} />}
          {lineRows.map((x) => (
            <div key={x.id} className="rr-voice__row" data-testid="voice-line">
              <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs" wrap="wrap">
                  <Text role="bodySmall" fw={650}>{x.number}</Text>
                  <Text role="bodySmall">{x.name}</Text>
                  {!x.is_active
                    ? <StatusPill tone="paused">{t('voice.lines.state.off')}</StatusPill>
                    : x.kind === 'forward'
                      ? <StatusPill tone="neutral">{t('voice.lines.state.forward')}</StatusPill>
                      : <StatusPill tone={x.signed_in ? 'active' : 'draft'}>{t(x.signed_in ? 'voice.lines.state.signedIn' : 'voice.lines.state.signedOut')}</StatusPill>}
                  {x.can_dial_out && <StatusPill tone="submitted">{t('voice.lines.state.dialOut')}</StatusPill>}
                </Group>
                <Text role="caption" c={MUTED}>
                  {x.kind === 'forward' ? t('voice.lines.forwardLine', { number: x.forward_to ?? '' }) : t(x.signed_in ? 'voice.lines.phoneLineIn' : 'voice.lines.phoneLineOut')}
                  {x.takes_transfers ? '' : ` · ${t('voice.lines.noTransfers')}`}
                </Text>
              </Stack>
              <Group gap={4} wrap="nowrap">
                {x.kind === 'phone' && (
                  <Button
                    size="xs" variant="default" loading={replace.isPending && replace.variables?.id === x.id}
                    onClick={() => openConfirmModal({
                      title: t('voice.lines.replaceTitle'),
                      children: <Text role="bodySmall">{t('voice.lines.replaceBody', { name: x.name })}</Text>,
                      labels: { confirm: t('voice.lines.replaceConfirm'), cancel: t('action.cancel') },
                      onConfirm: () => replace.mutate(x)
                    })}
                  >{t('voice.lines.replace')}</Button>
                )}
                <Button size="xs" variant="subtle" onClick={() => setLine(x)}>{t('action.edit')}</Button>
                <Button
                  size="xs" variant="subtle" color="error"
                  onClick={() => openConfirmModal({
                    title: t('voice.lines.removeTitle'),
                    children: <Text role="bodySmall">{t('voice.lines.removeBody', { name: x.name, number: x.number })}</Text>,
                    labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                    confirmProps: { color: 'red' },
                    onConfirm: () => removeLine.mutate(x)
                  })}
                >{t('action.delete')}</Button>
              </Group>
            </div>
          ))}
        </Stack>
      </Card>

      <Card withBorder data-testid="voice-groups">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
              <Title order={4}>{t('voice.groups.title')}</Title>
              <Text role="bodySmall" c={MUTED}>{t('voice.groups.intro')}</Text>
            </Stack>
            <Button size="xs" variant="default" disabled={lineRows.length === 0} onClick={() => setGroup('new')}>{t('voice.groups.add')}</Button>
          </Group>
          {groups.error && <Alert color="error" role="alert">{message(groups.error, t('common.unknownError'))}</Alert>}
          {!groups.isPending && groupRows.length === 0 && <EmptyState title={t('voice.groups.empty')} description={t('voice.groups.emptyHint')} />}
          {groupRows.map((g) => (
            <div key={g.id} className="rr-voice__row" data-testid="voice-group">
              <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs" wrap="wrap">
                  <Text role="bodySmall" fw={650}>{g.name}</Text>
                  {!g.is_active && <StatusPill tone="paused">{t('voice.lines.state.off')}</StatusPill>}
                </Group>
                <Text role="caption" c={MUTED}>
                  {g.extension_ids.length === 0
                    ? t('voice.groups.noLines')
                    : `${t(`voice.groups.strategyLine.${g.strategy}`, { seconds: g.ring_seconds })}: ${g.extension_ids.map(lineName).filter(Boolean).join(', ')}`}
                </Text>
                {g.purpose && <Text role="caption" c={MUTED}>{t('voice.groups.purposeLine', { purpose: g.purpose })}</Text>}
              </Stack>
              <Group gap={4} wrap="nowrap">
                <Button size="xs" variant="subtle" onClick={() => setGroup(g)}>{t('action.edit')}</Button>
                <Button
                  size="xs" variant="subtle" color="error"
                  onClick={() => openConfirmModal({
                    title: t('voice.groups.removeTitle'),
                    children: <Text role="bodySmall">{t('voice.groups.removeBody', { name: g.name })}</Text>,
                    labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                    confirmProps: { color: 'red' },
                    onConfirm: () => removeGroup.mutate(g)
                  })}
                >{t('action.delete')}</Button>
              </Group>
            </div>
          ))}
        </Stack>
      </Card>

      <LineForm line={line} outboundOn={status.switches.outbound} onClose={() => setLine(null)} onSaved={(x) => { refresh(); if (x.password) setShown(x); }} />
      <GroupForm group={group} lines={lineRows} onClose={() => setGroup(null)} onSaved={refresh} />
      <SignInDetails line={shown} onClose={() => setShown(null)} />
    </Stack>
  );
}

/** A phone's sign-in details, shown once: when the line is made and when they are replaced. */
function SignInDetails({ line, onClose }: { line: voice.VoiceExtension | null; onClose: () => void }) {
  const t = useT();
  const s = line?.sign_in;
  return (
    <Modal opened={line !== null} onClose={onClose} title={t('voice.lines.details.title')}>
      {line && s && (
        <Stack gap="md" data-testid="voice-line-details">
          <Alert color="warning">{t('voice.lines.details.once')}</Alert>
          <dl className="rr-voice__facts">
            <dt>{t('voice.lines.details.server')}</dt><dd>{s.server ?? t('voice.lines.details.serverLater')}</dd>
            <dt>{t('voice.lines.details.port')}</dt><dd>{s.port}</dd>
            <dt>{t('voice.lines.details.transport')}</dt><dd>{s.transport}</dd>
            <dt>{t('voice.lines.details.sound')}</dt><dd>{t('voice.lines.details.soundValue')}</dd>
            <dt>{t('voice.lines.details.username')}</dt><dd className="rr-voice__mono">{s.username}</dd>
            <dt>{t('voice.lines.details.password')}</dt><dd className="rr-voice__mono" data-testid="voice-line-password">{line.password}</dd>
          </dl>
          <Text role="caption" c={MUTED}>{t('voice.lines.details.help')}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { void navigator.clipboard?.writeText(`${s.server ?? ''}:${s.port} ${s.transport}\n${s.username}\n${line.password ?? ''}`); notifySuccess(t('voice.lines.details.copied')); }}>{t('voice.lines.details.copy')}</Button>
            <Button onClick={onClose}>{t('voice.lines.details.done')}</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function LineForm({ line, outboundOn, onClose, onSaved }: { line: voice.VoiceExtension | 'new' | null; outboundOn: boolean; onClose: () => void; onSaved: (x: voice.VoiceExtension) => void }) {
  const t = useT();
  const existing = line !== null && line !== 'new' ? line : null;
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'phone' | 'forward'>('phone');
  const [forwardTo, setForwardTo] = useState('');
  const [dialOut, setDialOut] = useState(false);
  const [transfers, setTransfers] = useState(true);
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (line === null) return;
    setNumber(existing?.number ?? '');
    setName(existing?.name ?? '');
    setKind(existing?.kind ?? 'phone');
    setForwardTo(existing?.forward_to ?? '');
    setDialOut(existing?.can_dial_out ?? false);
    setTransfers(existing?.takes_transfers ?? true);
    setActive(existing?.is_active ?? true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);

  const save = useMutation({
    mutationFn: () => {
      const input: voice.VoiceExtensionInput = {
        number: number.trim(), name: name.trim(), kind, forward_to: kind === 'forward' ? forwardTo.trim() : undefined,
        can_dial_out: kind === 'phone' ? dialOut : false, takes_transfers: transfers, is_active: active
      };
      return existing ? voice.updateExtension(existing.id, input) : voice.createExtension(input);
    },
    onSuccess: (res) => { onSaved(res.data); onClose(); if (!res.data.password) notifySuccess(t('voice.lines.saved')); },
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });

  return (
    <Drawer opened={line !== null} onClose={onClose} title={t(existing ? 'voice.lines.form.edit' : 'voice.lines.form.new')} position="right" size="lg">
      <Stack gap="md" data-testid="voice-line-form">
        {!existing && (
          <Stack gap={6}>
            <Text role="bodySmall" fw={600}>{t('voice.lines.form.kind')}</Text>
            <SegmentedControl
              value={kind} onChange={(v) => setKind(v as 'phone' | 'forward')}
              data={[{ value: 'phone', label: t('voice.lines.form.kind.phone') }, { value: 'forward', label: t('voice.lines.form.kind.forward') }]}
            />
            <Text role="caption" c={MUTED}>{t(`voice.lines.form.kindHint.${kind}`)}</Text>
          </Stack>
        )}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput label={t('voice.lines.form.number')} description={t('voice.lines.form.numberHint')} value={number} maxLength={4} placeholder="101" onChange={(e) => setNumber(e.currentTarget.value)} />
          <TextInput label={t('voice.lines.form.name')} description={t('voice.lines.form.nameHint')} value={name} maxLength={120} onChange={(e) => setName(e.currentTarget.value)} />
        </SimpleGrid>
        {kind === 'forward' && (
          <TextInput label={t('voice.lines.form.forwardTo')} description={t('voice.lines.form.forwardToHint')} value={forwardTo} placeholder="082 555 0100" onChange={(e) => setForwardTo(e.currentTarget.value)} />
        )}
        <Switch label={t('voice.lines.form.transfers')} description={t('voice.lines.form.transfersHint')} checked={transfers} onChange={(v) => setTransfers(v)} />
        {kind === 'phone' && (
          <Switch
            label={t('voice.lines.form.dialOut')}
            description={t(outboundOn ? 'voice.lines.form.dialOutHint' : 'voice.lines.form.dialOutUnavailable')}
            checked={dialOut} disabled={!outboundOn && !dialOut} onChange={(v) => setDialOut(v)}
          />
        )}
        {existing && <Switch label={t('voice.lines.form.active')} description={t('voice.lines.form.activeHint')} checked={active} onChange={(v) => setActive(v)} />}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={save.isPending} disabled={number.trim().length < 3 || name.trim().length === 0 || (kind === 'forward' && forwardTo.trim().length === 0)} onClick={() => save.mutate()}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function GroupForm({ group, lines, onClose, onSaved }: { group: voice.VoiceGroup | 'new' | null; lines: voice.VoiceExtension[]; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const existing = group !== null && group !== 'new' ? group : null;
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [strategy, setStrategy] = useState<'together' | 'in_order'>('together');
  const [seconds, setSeconds] = useState<number | ''>(25);
  const [members, setMembers] = useState<string[]>([]);

  useEffect(() => {
    if (group === null) return;
    setName(existing?.name ?? '');
    setPurpose(existing?.purpose ?? '');
    setStrategy(existing?.strategy ?? 'together');
    setSeconds(existing?.ring_seconds ?? 25);
    setMembers(existing?.extension_ids ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  const save = useMutation({
    mutationFn: () => {
      const input: voice.VoiceGroupInput = { name: name.trim(), purpose: purpose.trim(), strategy, ring_seconds: seconds === '' ? 25 : seconds, extension_ids: members };
      return existing ? voice.updateGroup(existing.id, input) : voice.createGroup(input);
    },
    onSuccess: () => { onSaved(); onClose(); notifySuccess(t('voice.groups.saved')); },
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });

  // Ticking a line adds it at the end, so the order ticked is the order rung.
  const toggle = (id: string, on: boolean) => setMembers((m) => (on ? [...m.filter((x) => x !== id), id] : m.filter((x) => x !== id)));

  return (
    <Drawer opened={group !== null} onClose={onClose} title={t(existing ? 'voice.groups.form.edit' : 'voice.groups.form.new')} position="right" size="lg">
      <Stack gap="md" data-testid="voice-group-form">
        <TextInput label={t('voice.groups.form.name')} description={t('voice.groups.form.nameHint')} value={name} maxLength={120} onChange={(e) => setName(e.currentTarget.value)} />
        <Textarea label={t('voice.groups.form.purpose')} description={t('voice.groups.form.purposeHint')} value={purpose} maxLength={300} minRows={2} onChange={(e) => setPurpose(e.currentTarget.value)} />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Select
            label={t('voice.groups.form.strategy')} description={t(`voice.groups.form.strategyHint.${strategy}`)}
            value={strategy} onChange={(e) => setStrategy(e.currentTarget.value as 'together' | 'in_order')}
            data={[{ value: 'together', label: t('voice.groups.form.strategy.together') }, { value: 'in_order', label: t('voice.groups.form.strategy.in_order') }]}
          />
          <NumberInput label={t('voice.groups.form.seconds')} description={t('voice.groups.form.secondsHint')} value={seconds} min={5} max={120} onChange={(v) => setSeconds(typeof v === 'number' ? v : '')} />
        </SimpleGrid>
        <Stack gap={6}>
          <Text role="bodySmall" fw={600}>{t('voice.groups.form.lines')}</Text>
          {lines.map((x) => (
            <Checkbox
              key={x.id} checked={members.includes(x.id)} onChange={(on) => toggle(x.id, on)}
              label={`${x.number} · ${x.name}${members.includes(x.id) && strategy === 'in_order' ? ` (${members.indexOf(x.id) + 1})` : ''}`}
            />
          ))}
          <Text role="caption" c={MUTED}>{t('voice.groups.form.linesHint')}</Text>
        </Stack>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={save.isPending} disabled={name.trim().length === 0} onClick={() => save.mutate()}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
